/**
 * A document Stripe asks for: the person's ID (`individual.verification.document`,
 * `person_….verification.document`), the company's (`company.verification.document`) or one of the
 * `documents.*` uploads. A document step can supply those; everything else (names, addresses,
 * phone numbers, and the proof of address Stripe may offer as an alternative) cannot.
 */
export function isDocumentRequirement(field) {
  return field.endsWith('.verification.document') || field.startsWith('documents.');
}

/**
 * Stripe's currently due fields, grouped by the reason Stripe gave for them. One failed identity
 * check names every keyed field with the same reason, so it is shown once above them instead of
 * once per field. Fields without a reason (simply missing) form their own group, with `reason` null.
 */
export function groupStripeRequirements(requirements) {
  const errors = requirements?.errors ?? [];

  return (requirements?.currently_due ?? []).reduce((groups, field) => {
    const reason = errors.find((error) => error.requirement === field)?.reason ?? null;
    const group = groups.find((item) => item.reason === reason);

    if (group) {
      group.fields.push(field);
    } else {
      groups.push({ reason, fields: [field] });
    }

    return groups;
  }, []);
}

// Whose detail a field is, by its first segment: the account holder of an individual account is the
// owner, a company account's people are its persons (`person_…`).
const SUBJECTS = [
  [/^individual\./, 'owner'],
  [/^(person_[^.]+|representative)\./, 'representative'],
  [/^(company|business_profile)\./, 'business'],
];

// The detail after the subject, by its longest known path (`address.line1` is the address,
// `dob.day` the date of birth).
const DETAILS = {
  first_name: 'first_name',
  last_name: 'last_name',
  email: 'email',
  phone: 'phone',
  dob: 'dob',
  id_number: 'id_number',
  nationality: 'nationality',
  address: 'address',
  'address.city': 'city',
  'address.postal_code': 'postal_code',
  'address.state': 'state',
  'address.country': 'country',
  'verification.document': 'id_document',
  'verification.additional_document': 'proof_of_address',
  'relationship.title': 'job_title',
  name: 'business_name',
  tax_id: 'tax_id',
  vat_id: 'vat_id',
  registration_number: 'registration_number',
  url: 'website',
  mcc: 'industry',
  product_description: 'product_description',
  support_phone: 'support_phone',
  owners_provided: 'owners',
  directors_provided: 'directors',
  executives_provided: 'executives',
};

// Fields that belong to no person or company.
const STANDALONE = {
  external_account: 'bank_account',
  business_type: 'business_type',
  'tos_acceptance.date': 'terms',
  'tos_acceptance.ip': 'terms',
  'documents.bank_account_ownership_verification.files': 'bank_account_proof',
  'documents.company_registration_verification.files': 'registration_document',
  'documents.proof_of_registration.files': 'registration_document',
  'documents.company_tax_id_verification.files': 'tax_id_document',
};

const humanize = (path) => {
  const words = path
    .split('.')
    .filter((segment) => segment !== 'files')
    .pop()
    .replace(/_/g, ' ');

  return words.charAt(0).toUpperCase() + words.slice(1);
};

/**
 * What a Stripe requirement code names, for people instead of `individual.address.city`:
 * `subject` (owner, representative, business or null) and `detail`, both keys of the step's
 * translations, or, for a field this list does not know, `fallback`: its last segment in words.
 */
export function describeStripeRequirement(field) {
  if (STANDALONE[field]) {
    return { subject: null, detail: STANDALONE[field], fallback: null };
  }

  const [, subject] = SUBJECTS.find(([pattern]) => pattern.test(field)) ?? [];

  if (!subject) {
    return { subject: null, detail: null, fallback: humanize(field) };
  }

  // The longest known path wins: `address.city` before `address`.
  const segments = field.split('.').slice(1);
  const path = segments
    .map((_, index) => segments.slice(0, segments.length - index).join('.'))
    .find((candidate) => DETAILS[candidate]);
  // A company's verification document is its registration, not an ID.
  const detail = subject === 'business' && path === 'verification.document' ? 'registration_document' : DETAILS[path];

  return { subject, detail: detail ?? null, fallback: detail ? null : humanize(field) };
}
